import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import {
  UserCircleIcon, CreditCardIcon, SparklesIcon,
  WalletIcon, ShoppingBagIcon, ClockIcon,
  CalendarIcon, ArrowRightIcon, ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const UserDashboard = () => {
  const { userData, currentUser } = useAuth();
  // Face scan state removed
  // const [showFaceScan, setShowFaceScan] = useState(false);

  const userName        = userData?.personalInfo?.name || currentUser?.displayName || 'User';
  const firstName       = userName.split(' ')[0];
  const membership      = userData?.membership || {};
  const membershipStatus = membership.status || 'inactive';
  const expiryDate      = membership.expiryDate
    ? new Date(membership.expiryDate).toLocaleDateString() : 'Not active';
  const walletBalance   = userData?.wallet?.balance || 0;
  // Face scan related variables removed
  // const hasFaceScan     = !!userData?.personalInfo?.facePhotoUrl;

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good Morning';
    if (h < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getMembershipBadge = () => {
    if (membershipStatus === 'active')  return { text: 'Active',   color: 'bg-emerald-500' };
    if (membershipStatus === 'expired') return { text: 'Expired',  color: 'bg-red-500' };
    return                                     { text: 'Inactive', color: 'bg-gray-500' };
  };

  const membershipBadge = getMembershipBadge();

  const userCards = [
    { title: 'My Profile',      description: 'View and manage your personal information', path: '/profile',  icon: <UserCircleIcon  className="h-6 w-6" />, bgColor: 'bg-blue-50',   iconColor: 'text-blue-600'   },
    { title: 'Payment History', description: 'View your payment records',                 path: '/payments', icon: <CreditCardIcon  className="h-6 w-6" />, bgColor: 'bg-amber-50',  iconColor: 'text-amber-600'  },
    { title: 'Order History',   description: 'Track your shop purchases',                 path: '/orders',   icon: <ShoppingBagIcon className="h-6 w-6" />, bgColor: 'bg-cyan-50',   iconColor: 'text-cyan-600'   },
    { title: 'Visit History',   description: 'View your gym access logs',                 path: '/visits',   icon: <ClockIcon       className="h-6 w-6" />, bgColor: 'bg-orange-50', iconColor: 'text-orange-600' },
  ];

  const containerVariants = {
    hidden:  { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
  };
  const itemVariants = {
    hidden:  { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* ── Welcome ── */}
        <motion.div initial={{ opacity: 0, y: -15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-3xl">💪</span>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  {getGreeting()}, <span className="text-primary">{firstName}</span>
                </h1>
              </div>
              <div className="flex items-center space-x-2">
                <p className="text-gray-500 text-sm">Welcome to your fitness journey</p>
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium text-white ${membershipBadge.color}`}>
                  {membershipBadge.text}
                </span>
              </div>
            </div>
            <div className="flex flex-col items-end">
              <div className="flex items-center space-x-2 text-gray-600">
                <ClockIcon className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-gray-400 text-xs mt-0.5">
                <CalendarIcon className="h-3 w-3" />
                <span>{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ── FACE SCAN SECTIONS COMPLETELY REMOVED ── */}
        {/* The face scan notice and face scan status sections are both removed */}

        {/* ── Quick Stats ── */}
        <motion.div
          initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">

          <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl border border-primary/20 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 mb-1">Wallet Balance</p>
                <p className="text-2xl font-bold text-gray-900">KES {walletBalance.toLocaleString()}</p>
              </div>
              <div className="p-2 bg-primary/10 rounded-lg">
                <WalletIcon className="h-6 w-6 text-primary" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl border border-primary/20 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 mb-1">Membership Status</p>
                <p className="text-xl font-bold text-gray-900 capitalize">{membershipStatus}</p>
                {membershipStatus === 'active' && (
                  <p className="text-xs text-gray-500 mt-1">Expires: {expiryDate}</p>
                )}
              </div>
              <div className="p-2 bg-primary/10 rounded-lg">
                <SparklesIcon className="h-6 w-6 text-primary" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* ── Nav Cards ── */}
        <motion.div variants={containerVariants} initial="hidden" animate="visible"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {userCards.map(card => (
            <motion.div key={card.title} variants={itemVariants}
              whileHover={{ y: -3 }} transition={{ type: 'spring', stiffness: 400, damping: 25 }}>
              <Link to={card.path}>
                <div className="group relative bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 hover:border-primary overflow-hidden">
                  <div className="absolute inset-x-0 top-0 h-0.5 bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        <div className={`p-2 rounded-lg ${card.bgColor} group-hover:scale-105 transition-transform duration-300`}>
                          <div className={card.iconColor}>{card.icon}</div>
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-gray-900 group-hover:text-primary transition-colors duration-300">{card.title}</h3>
                          <p className="text-xs text-gray-500 mt-0.5">{card.description}</p>
                        </div>
                      </div>
                      <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:shadow-md transition-all duration-300">
                        <ArrowRightIcon className="h-3.5 w-3.5 text-primary group-hover:text-white transition-all duration-300" />
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* ── Face Scan Modal - COMPLETELY REMOVED ── */}
    </div>
  );
};

export default UserDashboard;